package scenes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Slider
import androidx.compose.material3.SliderDefaults
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.*
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import domain.player.GameAction
import domain.player.GraphicsConfig
import domain.player.KeyBindingStore
import domain.player.PlayerSession
import engine.model.HandlingConfig
import kotlinx.coroutines.delay
import rendering.AnimatedBackground
import rendering.fontMono
import utils.isMobileDevice

private val COLOR_ACCENT = Color(0xFF00E5FF)
private val COLOR_LABEL = Color(0xFF888888)
private val COLOR_VALUE = Color(0xFFFFFFFF)
private val COLOR_SECTION = Color(0xFF444466)
private val COLOR_BORDER = Color(0xFF333355)

@Composable
fun SettingsScene(navigate: Navigate) {
    val focusRequester = remember { FocusRequester() }
    val scrollState = rememberScrollState()

    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.05.sp)

    // Load initial state from PlayerSession
    val initialHandling = remember { PlayerSession.profile.handling }
    val initialGraphics = remember { PlayerSession.profile.graphics }
    val initialBindings = remember { KeyBindingStore.loadActive() }

    // Mutable handling state
    var dasDelay by remember { mutableStateOf(initialHandling.dasDelay.toFloat()) }
    var arrDelay by remember { mutableStateOf(initialHandling.arrDelay.toFloat()) }
    var softDropFactor by remember { mutableStateOf(initialHandling.softDropFactor.toFloat()) }
    var dcdDelay by remember { mutableStateOf(initialHandling.dcdDelay.toFloat()) }
    var dasCutOnRotation by remember { mutableStateOf(initialHandling.dasCutOnRotation) }
    var preventAccidentalHardDrop by remember { mutableStateOf(initialHandling.preventAccidentalHardDrop) }

    // Mutable graphics state
    var boardShake by remember { mutableStateOf(initialGraphics.boardShake.toFloat()) }
    var bgOpacity by remember { mutableStateOf(initialGraphics.backgroundOpacity.toFloat()) }

    // Mutable key bindings state (action -> list of key codes)
    val bindings = remember { mutableStateMapOf<GameAction, List<String>>().also { it.putAll(initialBindings) } }

    // Rebind state: which action is being rebound
    var rebindingAction by remember { mutableStateOf<GameAction?>(null) }
    val rebindFocusRequester = remember { FocusRequester() }

    fun saveAndExit() {
        PlayerSession.updateHandling(
            HandlingConfig(
                dasDelay = dasDelay.toDouble(),
                arrDelay = arrDelay.toDouble(),
                softDropFactor = softDropFactor.toDouble(),
                dcdDelay = dcdDelay.toDouble(),
                dasCutOnRotation = dasCutOnRotation,
                preventAccidentalHardDrop = preventAccidentalHardDrop,
            )
        )
        PlayerSession.updateGraphics(GraphicsConfig(boardShake = boardShake.toDouble(), backgroundOpacity = bgOpacity.toDouble()))
        PlayerSession.updateKeyBindings(bindings.toMap())
        navigate(SceneName.MENU, null)
    }

    LaunchedEffect(Unit) {
        delay(50)
        focusRequester.requestFocus()
    }
    LaunchedEffect(rebindingAction) {
        if (rebindingAction != null) rebindFocusRequester.requestFocus()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
            .focusRequester(focusRequester)
            .focusable()
            .onKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                when {
                    rebindingAction != null -> false // let rebind overlay handle it
                    event.key == Key.Escape -> {
                        navigate(SceneName.MENU, null)
                        true
                    }
                    event.key == Key.S -> {
                        saveAndExit()
                        true
                    }
                    else -> false
                }
            },
    ) {
        AnimatedBackground(modifier = Modifier.fillMaxSize())

        Column(modifier = Modifier.fillMaxSize()) {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "SETTINGS",
                    style = monoStyle.copy(fontSize = 20.sp, color = COLOR_ACCENT, letterSpacing = 0.15.sp),
                )
                Text(
                    text = "Esc to go back  •  S to save",
                    style = monoStyle.copy(fontSize = 9.sp, color = COLOR_LABEL),
                )
            }

            // Scrollable content
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .verticalScroll(scrollState)
                    .padding(horizontal = 24.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                // ── HANDLING ──────────────────────────────────────────────────
                SectionHeader("HANDLING")

                SliderRow(
                    label = "DAS",
                    value = dasDelay,
                    valueRange = 50f..300f,
                    displayValue = "${dasDelay.toInt()} ms",
                    onValueChange = { dasDelay = it },
                )
                SliderRow(
                    label = "ARR",
                    value = arrDelay,
                    valueRange = 0f..100f,
                    displayValue = "${arrDelay.toInt()} ms",
                    onValueChange = { arrDelay = it },
                )
                SliderRow(
                    label = "SDF",
                    value = softDropFactor,
                    valueRange = 1f..40f,
                    displayValue = "${softDropFactor.toInt()}×",
                    onValueChange = { softDropFactor = it },
                )
                SliderRow(
                    label = "DCD",
                    value = dcdDelay,
                    valueRange = 0f..200f,
                    displayValue = "${dcdDelay.toInt()} ms",
                    onValueChange = { dcdDelay = it },
                )
                ToggleRow(
                    label = "CUT DAS ON ROTATION",
                    checked = dasCutOnRotation,
                    onCheckedChange = { dasCutOnRotation = it },
                )
                ToggleRow(
                    label = "PREVENT ACCIDENTAL HARD DROP",
                    checked = preventAccidentalHardDrop,
                    onCheckedChange = { preventAccidentalHardDrop = it },
                )

                Spacer(modifier = Modifier.height(8.dp))

                // ── CONTROLS ──────────────────────────────────────────────────
                if (!isMobileDevice()) {
                    SectionHeader("CONTROLS")

                    GameAction.entries.forEach { action ->
                        val keys = bindings[action] ?: emptyList()
                        val keysDisplay = keys
                            .joinToString(" / ") { KeyBindingStore.formatKeyCode(it) }
                            .ifEmpty { "—" }

                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 4.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(
                                text = action.displayName,
                                style = monoStyle.copy(fontSize = 10.sp, color = COLOR_LABEL),
                                modifier = Modifier.weight(1f),
                            )
                            Box(
                                modifier = Modifier
                                    .border(width = 1.dp, color = COLOR_BORDER)
                                    .padding(horizontal = 8.dp, vertical = 4.dp),
                            ) {
                                Text(
                                    text = keysDisplay,
                                    style = monoStyle.copy(fontSize = 10.sp, color = COLOR_VALUE),
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(8.dp))
                }

                // ── GRAPHICS ──────────────────────────────────────────────────
                SectionHeader("GRAPHICS")

                SliderRow(
                    label = "BOARD SHAKE",
                    value = boardShake,
                    valueRange = 0f..1f,
                    displayValue = "${(boardShake * 100).toInt()}%",
                    onValueChange = { boardShake = it },
                )
                SliderRow(
                    label = "BG OPACITY",
                    value = bgOpacity,
                    valueRange = 0f..1f,
                    displayValue = if (bgOpacity < 0.05f) "OFF" else "${(bgOpacity * 100).toInt()}%",
                    onValueChange = { bgOpacity = it },
                )

                Spacer(modifier = Modifier.height(24.dp))

                // ── Save button ───────────────────────────────────────────────
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { saveAndExit() }
                        .border(width = 1.dp, color = COLOR_ACCENT)
                        .padding(vertical = 14.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = "SAVE & APPLY  [S]",
                        style = monoStyle.copy(fontSize = 14.sp, color = COLOR_ACCENT, letterSpacing = 0.1.sp),
                        textAlign = TextAlign.Center,
                    )
                }

                Spacer(modifier = Modifier.height(32.dp))
            }
        }

        // ── Rebind overlay ────────────────────────────────────────────────────
        val action = rebindingAction
        if (action != null) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color(0xDD000000))
                    .focusRequester(rebindFocusRequester)
                    .focusable()
                    .onKeyEvent { event ->
                        if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                        if (event.key == Key.Escape) {
                            rebindingAction = null
                            return@onKeyEvent true
                        }
                        // Assign the pressed key as the sole binding for this action
                        val newCode = event.key.keyCode.toString()
                        bindings[action] = listOf(newCode)
                        rebindingAction = null
                        true
                    },
                contentAlignment = Alignment.Center,
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        text = "REBINDING",
                        style = monoStyle.copy(fontSize = 11.sp, color = COLOR_LABEL, letterSpacing = 0.2.sp),
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    Text(
                        text = action.displayName,
                        style = monoStyle.copy(fontSize = 18.sp, color = COLOR_ACCENT),
                        textAlign = TextAlign.Center,
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        text = "Press any key...",
                        style = monoStyle.copy(fontSize = 13.sp, color = COLOR_VALUE),
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = "Esc to cancel",
                        style = monoStyle.copy(fontSize = 9.sp, color = COLOR_LABEL),
                    )
                }
            }
        }
    }
}

// ── Helper composables ────────────────────────────────────────────────────────

@Composable
private fun SectionHeader(title: String) {
    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.05.sp)
    Spacer(modifier = Modifier.height(8.dp))
    Text(
        text = title,
        style = monoStyle.copy(
            fontSize = 11.sp,
            color = COLOR_SECTION,
            letterSpacing = 0.2.sp,
        ),
    )
    Spacer(modifier = Modifier.height(4.dp))
}

@Composable
private fun SliderRow(
    label: String,
    value: Float,
    valueRange: ClosedFloatingPointRange<Float>,
    displayValue: String,
    onValueChange: (Float) -> Unit,
) {
    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.05.sp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = label,
            style = monoStyle.copy(fontSize = 10.sp, color = COLOR_LABEL),
            modifier = Modifier.width(80.dp),
        )
        Slider(
            value = value,
            onValueChange = onValueChange,
            valueRange = valueRange,
            modifier = Modifier.weight(1f),
            colors = SliderDefaults.colors(
                thumbColor = COLOR_ACCENT,
                activeTrackColor = COLOR_ACCENT,
                inactiveTrackColor = COLOR_BORDER,
            ),
        )
        Text(
            text = displayValue,
            style = monoStyle.copy(fontSize = 10.sp, color = COLOR_VALUE),
            modifier = Modifier.width(56.dp),
            textAlign = TextAlign.End,
        )
    }
}

@Composable
private fun ToggleRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.05.sp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            text = label,
            style = monoStyle.copy(fontSize = 10.sp, color = COLOR_LABEL),
            modifier = Modifier.weight(1f),
        )
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = COLOR_ACCENT,
                checkedTrackColor = COLOR_ACCENT.copy(alpha = 0.4f),
                uncheckedThumbColor = COLOR_LABEL,
                uncheckedTrackColor = COLOR_BORDER,
            ),
        )
    }
}
